import React, { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../hooks/useSocket';
import { useVoiceChat } from '../hooks/useVoiceChat';
import VideoPlayer from './VideoPlayer';
import Chat from './Chat';
import UserList from './UserList';
import './RoomPage.css';

const API = process.env.REACT_APP_API_URL || '';

export default function RoomPage({ roomId, navigate }) {
  const { socketRef, emit, on, onReconnect, onVisible } = useSocket();

  const [roomData, setRoomData] = useState(null);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [userName, setUserName] = useState('');
  const [joined, setJoined] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('chat');

  const [showChangeModal, setShowChangeModal] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');

  const inviteUrl = window.location.href;

  // Загрузка комнаты
  useEffect(() => {
    fetch(`${API}/api/rooms/${roomId}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setRoomData(data);
      })
      .catch(() => setError('Не удалось подключиться к серверу'));
  }, [roomId]);

  // Автозаполнение имени
  useEffect(() => {
    const saved = sessionStorage.getItem('watchparty_username');
    if (saved) setNameInput(saved);
  }, []);

  // Подписка на сокеты
  useEffect(() => {
    if (!joined) return;

    const offState = on('room_state', (data) => {
      setRoomData(prev => ({
        ...prev,
        videoId: data.videoId,
        platform: data.platform || 'rutube',
        oid: data.oid || null,
        hash: data.hash || null,
        state: data.state,
      }));
      setUsers(data.users || []);
      setMessages(data.messages || []);
    });

    const offUsers = on('users_updated', (u) => setUsers(u || []));

    const offMsg = on('chat_message', (msg) => setMessages(prev => [...prev, msg]));

    const offChanged = on('video_changed', (newData) => {
      console.log('✅ [video_changed] Видео успешно сменено на сервере');
      setRoomData(prev => (prev ? { ...prev, ...newData } : newData));
    });

    return () => {
      offState();
      offUsers();
      offMsg();
      offChanged();
    };
  }, [joined, on]);

  // Присоединение к комнате
  const joinRoom = useCallback((name) => {
    setUserName(name);
    setJoined(true);

    setTimeout(() => {
      const doJoin = () => {
        emit('join_room', { roomId, userName: name });
        setTimeout(() => emit('get_video_state', { roomId }), 300);
      };

      if (socketRef.current?.connected) doJoin();
      else {
        const interval = setInterval(() => {
          if (socketRef.current?.connected) {
            clearInterval(interval);
            doJoin();
          }
        }, 200);
        setTimeout(() => clearInterval(interval), 15000);
      }
    }, 10);
  }, [roomId, socketRef, emit]);

  const handleJoin = (e) => {
    e?.preventDefault();
    const name = nameInput.trim() || `Гость_${Math.floor(Math.random() * 1000)}`;
    sessionStorage.setItem('watchparty_username', name);
    joinRoom(name);
  };

  const { inVoice, voiceUsers, muted, volumes, joinVoice, leaveVoice, toggleMute, setUserVolume } = useVoiceChat({ socketRef, roomId });
  
  useEffect(() => {
    if (!joined || !userName) return;
    // Единственный onReconnect — объединяем rejoin комнаты и восстановление голоса.
    // Два отдельных вызова onReconnect перезаписывали друг друга, и join_room не выполнялся.
    onReconnect(() => {
      console.log('[reconnect] rejoining room...');
      emit('join_room', { roomId, userName });
      setTimeout(() => emit('get_video_state', { roomId }), 300);

      if (inVoice) {
        setTimeout(() => socketRef.current?.emit('voice_joined', { roomId }), 1000);
      }
    });
  }, [joined, userName, inVoice, onReconnect, emit, roomId, socketRef]);

  const [changeVideoError, setChangeVideoError] = useState('');

  // Обработчик ошибок от сервера (например, невалидная ссылка при смене видео)
  useEffect(() => {
    if (!joined) return;
    const offError = on('error', (msg) => {
      console.warn('[socket] server error:', msg);
      // Показываем ошибку в модалке смены видео если она открыта, иначе alert
      if (showChangeModal) {
        setChangeVideoError(typeof msg === 'string' ? msg : 'Ошибка сервера');
      } else {
        alert(typeof msg === 'string' ? msg : 'Ошибка сервера');
      }
    });
    return () => offError();
  }, [joined, on, showChangeModal]);

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendMessage = useCallback((text) => emit('chat_message', { roomId, text }), [emit, roomId]);

  // Смена видео
  const handleChangeVideo = (e) => {
    e.preventDefault();
    const url = newVideoUrl.trim();
    if (!url) return;

    // Клиентская валидация до отправки на сервер
    const isRutube = url.includes('rutube.ru/video/');
    const isYouTube = url.includes('youtube.com/watch') ||
      url.includes('youtu.be/') ||
      url.includes('youtube.com/embed') ||
      url.includes('youtube.com/shorts');

    if (!isRutube && !isYouTube) {
      setChangeVideoError('Поддерживаются ссылки Rutube и YouTube');
      return;
    }

    console.log('🔥 handleChangeVideo → отправляем change_video', url);
    setChangeVideoError('');
    emit('change_video', { roomId, videoUrl: url });
    setNewVideoUrl('');
    setShowChangeModal(false);
  };

  if (error) {
    return (
      <div className="room-error">
        <div className="room-error-box">
          <div className="error-icon">✕</div>
          <h2>Комната не найдена</h2>
          <p>{error}</p>
          <button className="btn-back" onClick={() => navigate('/')}>← На главную</button>
        </div>
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="room-loading">
        <div className="loading-spinner" />
        <p>Загрузка комнаты...</p>
      </div>
    );
  }

  if (!joined) {
    return (
      <div className="room-join-overlay">
        <div className="room-bg-grid" />
        <div className="join-card">
          <div className="join-card-top">
            <div className="join-logo">▶ WATCHPARTY</div>
            <div className="join-room-id">КОМНАТА #{roomId}</div>
          </div>
          <p className="join-subtitle">Введите имя чтобы войти</p>
          <form onSubmit={handleJoin} className="join-form">
            <input
              className="form-input"
              type="text"
              placeholder="Ваше имя..."
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              maxLength={30}
              autoFocus
            />
            <button className="btn-create" type="submit">
              <span>▶</span> Войти в комнату
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="room-page">
      <header className="room-header">
        <button className="room-back" onClick={() => navigate('/')}>← WatchParty</button>
        <div className="room-header-center">
          <span className="room-id-badge">#{roomId}</span>
          <span className="room-user-count">
            {users.length} {users.length === 1 ? 'зритель' : 'зрителей'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="invite-btn" onClick={copyInvite}>
            {copied ? '✓ Скопировано!' : '🔗 Пригласить'}
          </button>
          <button className="invite-btn" onClick={() => setShowChangeModal(true)}>
            📼 Сменить видео
          </button>
        </div>
      </header>

      <div className="room-body">
        <main className="room-main">
          {roomData?.videoId ? (
            <VideoPlayer
              key={roomData.videoId}
              videoId={roomData.videoId}
              platform={roomData.platform}
              oid={roomData.oid}
              hash={roomData.hash}
              initialState={roomData.state}
              roomId={roomId}
              emit={emit}
              on={on}
            />
          ) : (
            <div className="video-wrapper" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#888', fontSize: '15px' }}>
              <div className="loading-spinner" />
              <span style={{ marginLeft: '12px' }}>Загрузка видео...</span>
            </div>
          )}
        </main>

        <aside className="room-sidebar">
          <div className="sidebar-tabs">
            <button className={`sidebar-tab ${sidebarTab === 'chat' ? 'active' : ''}`} onClick={() => setSidebarTab('chat')}>💬 Чат</button>
            <button className={`sidebar-tab ${sidebarTab === 'users' ? 'active' : ''}`} onClick={() => setSidebarTab('users')}>👥 Участники ({users.length})</button>
          </div>

          {sidebarTab === 'chat' && <Chat messages={messages} onSend={sendMessage} userName={userName} />}
          {sidebarTab === 'users' && <UserList users={users} voiceUsers={voiceUsers} />}

          <div className="voice-panel">
            <div className="voice-panel-label">🎙️ ГОЛОСОВОЙ ЧАТ</div>
            {inVoice ? (
              <div className="voice-controls">
                <div className="voice-status">
                  <span className={`voice-dot ${inVoice ? 'active' : ''}`} />
                  <span>Вы в голосовом чате</span>
                  {Object.keys(voiceUsers).length > 0 && <span className="voice-count">👥 {Object.keys(voiceUsers).length}</span>}
                </div>

                {Object.values(voiceUsers).length > 0 && (
                  <div className="voice-users-list">
                    <div className="voice-users-title">Участники:</div>
                    {Object.values(voiceUsers).map(user => (
                      <div key={user.id} className="voice-user-item">
                        <div className="voice-user-info">
                          <span className="voice-user-name">{user.name || 'Гость'}</span>
                          <span className="voice-user-status">🔊</span>
                        </div>
                        <div className="voice-volume-control">
                          <span className="volume-icon">🔊</span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volumes[user.id] ?? 1}
                            onChange={(e) => setUserVolume(user.id, parseFloat(e.target.value))}
                            className="voice-volume-slider"
                          />
                          <span className="volume-value">
                            {Math.round((volumes[user.id] ?? 1) * 100)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="voice-buttons">
                  <button className={`voice-mute-btn ${muted ? 'muted' : ''}`} onClick={toggleMute}>
                    {muted ? '🔇' : '🎤'}
                  </button>
                  <button className="voice-leave-btn" onClick={leaveVoice}>
                    🚪 Выйти
                  </button>
                </div>
              </div>
            ) : (
              <button className="voice-join-btn" onClick={joinVoice}>
                🎤 Подключиться к голосовому чату
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* МОДАЛКА СМЕНЫ ВИДЕО */}
      {showChangeModal && (
        <div className="room-join-overlay" style={{ zIndex: 100 }}>
          <div className="join-card" style={{ maxWidth: 460 }}>
            <div className="join-card-top">
              <div className="join-logo">📼 СМЕНА ВИДЕО</div>
            </div>
            <p className="join-subtitle">Вставьте новую ссылку (Rutube / YouTube)</p>
            <form onSubmit={handleChangeVideo} className="join-form">
              <input
                className="form-input"
                type="text"
                placeholder="https://rutube.ru/video/... или YouTube"
                value={newVideoUrl}
                onChange={e => { setNewVideoUrl(e.target.value); setChangeVideoError(''); }}
                autoFocus
              />
              {changeVideoError && (
                <div className="form-error">{changeVideoError}</div>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn-back" onClick={() => { setShowChangeModal(false); setChangeVideoError(''); }}>
                  Отмена
                </button>
                <button type="submit" className="btn-create">
                  Поменять видео
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}